import axios from "axios";
import HelperService from "../services/helper-service";
import { KuikaAppManager } from "../shared/utilty/kuika-app-manager";
import LocalStorageService from "./local-storage-service";

declare const window: any;

const backendUrl = KuikaAppManager.GetBackendUrl();
let isTokenRefreshing = false;
let failedQueue = [] as any;
const localStorageService = LocalStorageService.getService();

const MAX_RETRIES = -1; // -1 for infinite retries
const RETRY_DELAY = 3000;

axios.interceptors.response.use(undefined, async (error) => {
  const config = error.config;

  config.__retryCount = config.__retryCount || 0;

  const shouldRetry = !error.response || error.code === "ECONNREFUSED" || error.code === "ERR_NETWORK";

  console.log("Retry check:", {
    shouldRetry,
    retryCount: config.__retryCount,
    error: error.message || error.code
  });

  if (shouldRetry && (MAX_RETRIES === -1 || config.__retryCount < MAX_RETRIES)) {
    config.__retryCount += 1;

    console.log(`Retrying request (${config.__retryCount}), waiting ${RETRY_DELAY}ms...`);

    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));

    return axios(config);
  }

  return Promise.reject(error);
});

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Add a request interceptor
axios.interceptors.request.use(
  (config) => {
    const urlItems = document.location.pathname.substring(1).split(/\//);
    const token = localStorageService.getAccessToken();
    const language = localStorage.getItem("ml");
    if (language && language.length) {
      config.headers.cultureName = language;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.domainName = urlItems[0];
    }
    if (window.kuika && window.kuika?.project && window.kuika?.project.domainId) {
      config.headers.domainId = window.kuika?.project.domainId;
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

// Add a response interceptor
axios.interceptors.response.use(
  async (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      return Promise.reject(error);
    }

    if (error.response.status === 401 && originalRequest.url === `${backendUrl}/auth/login`) {
      return Promise.reject(error);
    }

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      if (isTokenRefreshing === false) {
        isTokenRefreshing = true;
        const refreshToken = localStorageService.getRefreshToken();

        if (!refreshToken || refreshToken === "null" || refreshToken === "") {
          localStorageService.clearToken();

          if (!HelperService.getIsInLoginPage()) {
            const redirectUri = window.location.pathname + window.location.search;
            HelperService.gotoLogin(redirectUri);
          }

          return;
        }

        return axios
          .post(`${backendUrl}/auth/refreshtoken`, {
            refreshtoken: refreshToken
          })
          .then((res) => {
            isTokenRefreshing = false;
            if (res && res.status === 200) {
              localStorageService.setToken(res.data);
              axios.defaults.headers.common.Authorization = `Bearer ${localStorageService.getAccessToken()}`;
              processQueue(null, res.data.token);
              return axios(originalRequest);
            }
          })
          .catch((e) => {
            localStorageService.clearToken();
            HelperService.gotoLogin();
          });
      }
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;

          if (originalRequest.url === `${backendUrl}/auth/getistokenvalid`) {
            const refreshToken = localStorageService.getRefreshToken();
            if (refreshToken && originalRequest.data) {
              originalRequest.data = {
                token: localStorage.getItem("refreshToken")
              };
            }
          }

          return axios(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    if (error.response.status >= 400 && error.config.url.indexOf("refreshtoken") > -1) {
      localStorageService.clearToken();
      HelperService.gotoLogin();
    }
    return Promise.reject(error);
  }
);
